import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { useSafetyStockApply } from '../components/SafetyStockModuleLayout';
import { RunStatusBar } from '../components/RunStatusBar';
import { SafetyStockKpiCards } from '../components/SafetyStockKpiCards';
import { BreakdownSection } from '../components/BreakdownSection';
import { AbcXyzMatrix } from '../components/AbcXyzMatrix';
import { AlertsSection } from '../components/AlertsSection';
import {
  safetyStockRunsQueryKey,
  useSafetyStockConfig,
  useSafetyStockScope,
} from '../hooks/useSafetyStockConfig';
import {
  pickLatestAnalyzableRun,
  pickLatestRun,
  useSafetyStockBreakdown,
  useSafetyStockCurrentSummary,
  useSafetyStockRunDetail,
  useSafetyStockRunErrors,
  useSafetyStockRunSummary,
  useSafetyStockRunsList,
} from '../hooks/useSafetyStockSummary';

export function SafetyStockSummaryPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const warehouseId = useWarehouseId();
  const { isActionEnabled } = useFeatureVisibility();
  const applyEnabled = isActionEnabled('safety_stock.apply', false);
  const applyFlow = useSafetyStockApply();

  const [dimension, setDimension] = useState('ABC_XYZ');

  useEffect(() => {
    if (!applyFlow.isRunning) return;
    void queryClient.invalidateQueries({ queryKey: safetyStockRunsQueryKey });
  }, [applyFlow.isRunning, queryClient]);

  useEffect(() => {
    if (!applyFlow.flowComplete) return;
    void queryClient.invalidateQueries({ queryKey: safetyStockRunsQueryKey });
  }, [applyFlow.flowComplete, queryClient]);

  const configQuery = useSafetyStockConfig();
  const scopeQuery = useSafetyStockScope(
    configQuery.data?.config.active_products_only ?? true,
    warehouseId,
    Boolean(configQuery.data?.config),
  );
  const runsQuery = useSafetyStockRunsList(warehouseId, 15);
  const latestRun = useMemo(
    () => pickLatestRun(runsQuery.data?.results),
    [runsQuery.data?.results],
  );
  const analyzableRun = useMemo(
    () => pickLatestAnalyzableRun(runsQuery.data?.results),
    [runsQuery.data?.results],
  );

  const isRunning = latestRun?.status === 'RUNNING' || applyFlow.isRunning;
  const analyzableRunId =
    analyzableRun &&
    (analyzableRun.status === 'SUCCESS' || analyzableRun.status === 'PARTIAL_SUCCESS') &&
    analyzableRun.summary_ready
      ? analyzableRun.run_id
      : null;

  const runDetailQuery = useSafetyStockRunDetail(
    latestRun?.run_id,
    latestRun?.status === 'RUNNING',
  );
  const summaryQuery = useSafetyStockRunSummary(analyzableRunId, warehouseId);
  const currentQuery = useSafetyStockCurrentSummary(warehouseId, !analyzableRunId);
  const breakdownQuery = useSafetyStockBreakdown(analyzableRunId, dimension, warehouseId);
  const abcBreakdownQuery = useSafetyStockBreakdown(
    analyzableRunId,
    'ABC_XYZ',
    warehouseId,
  );
  const errorsQuery = useSafetyStockRunErrors(
    analyzableRunId,
    warehouseId,
    Boolean(analyzableRunId),
  );

  // If selected dimension is no longer available (e.g. cat disabled), fall back.
  useEffect(() => {
    if (!dimension.startsWith('CAT_')) return;
    const enabled = breakdownQuery.data?.enabled_cat_fields || [];
    if (enabled.length && !enabled.includes(dimension) && !breakdownQuery.isFetching) {
      setDimension('ABC_XYZ');
    }
  }, [dimension, breakdownQuery.data?.enabled_cat_fields, breakdownQuery.isFetching]);

  const summaryRow =
    summaryQuery.data?.summary ||
    (!analyzableRunId ? currentQuery.data?.summary : null) ||
    null;

  const noEngineRun =
    !analyzableRun && !latestRun && !applyFlow.isRunning;
  const kpisLoading =
    runsQuery.isLoading ||
    (analyzableRunId ? summaryQuery.isLoading : currentQuery.isLoading);

  const sourceHint = analyzableRunId
    ? isRunning
      ? t('safetyStock.summary.sourcePreviousRun')
      : null
    : currentQuery.data?.notice || t('safetyStock.summary.sourcePolicy');

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {runsQuery.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('safetyStock.summary.loadError')}
        </div>
      ) : null}

      <RunStatusBar
        run={latestRun}
        detail={
          applyFlow.isRunning
            ? applyFlow.runStatus
            : runDetailQuery.data
        }
        loading={runsQuery.isLoading}
        applyEnabled={applyEnabled}
        applyRunning={applyFlow.isRunning}
        itemCount={scopeQuery.data?.total_items}
        noEngineRun={!latestRun && !applyFlow.isRunning}
        policyNotice={
          !latestRun && !applyFlow.isRunning
            ? t('safetyStock.summary.runStatus.noRun')
            : undefined
        }
        onRecalculate={() =>
          applyFlow.requestApply(scopeQuery.data?.total_items ?? null)
        }
        onViewProgress={applyFlow.reopenProgress}
      />

      {!latestRun && !analyzableRun && !runsQuery.isLoading ? (
        <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p>{t('safetyStock.summary.empty.title')}</p>
            <p className="mt-1 text-sky-800/80">{t('safetyStock.summary.empty.hint')}</p>
          </div>
        </div>
      ) : null}

      {latestRun && !analyzableRunId && !isRunning ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t('safetyStock.summary.waitingSummary', { status: latestRun.status })}
        </div>
      ) : null}

      <SafetyStockKpiCards
        summary={summaryRow}
        loading={kpisLoading}
        sourceHint={sourceHint}
      />

      <AbcXyzMatrix
        rows={abcBreakdownQuery.data?.rows || []}
        loading={Boolean(analyzableRunId) && abcBreakdownQuery.isLoading}
        disabled={!analyzableRunId}
      />

      <BreakdownSection
        dimension={dimension}
        onDimensionChange={setDimension}
        rows={breakdownQuery.data?.rows || []}
        totalValue={summaryRow?.safety_stock_value}
        loading={Boolean(analyzableRunId) && breakdownQuery.isLoading}
        disabled={!analyzableRunId}
      />

      {analyzableRunId ? (
        <AlertsSection
          summary={summaryRow}
          errorsTotal={errorsQuery.data?.total}
        />
      ) : null}

      {noEngineRun && summaryRow ? (
        <p className="text-xs text-gray-500">{t('safetyStock.summary.policyDisclaimer')}</p>
      ) : null}
    </div>
  );
}
